import { AreaSlugMap } from '~/constants'
import { useGetLpisAndPercentilesQuery } from '~/graphql/generated/schema'
import { getAreaSlugList } from '~/utils/areaSlugUtils'

const MaxLpi = 2000
const AreaSlugList = getAreaSlugList('stats-page')

type AreaProps = {
  areaSlug: AreaSlugType
  notIncludedInLPI: boolean
  progress: number
  relativeProgress: number
  lpi: number
  lpiText: string
  hasLpi: boolean
}

type FilteredAreaType = {
  areaSlug: AreaSlugType
  lpi: number
}

export const useLpiSummary = () => {
  const { loading, error, data } = useGetLpisAndPercentilesQuery()
  const lpiSummary = data?.me?.lpiSummary

  const overallLpi = lpiSummary?.overallLpi || 0
  const overallLpiText = overallLpi.toFixed(0)
  const overallPercent = (overallLpi / MaxLpi) * 100 // do not round this, as 1714(overall) and 1728(best) are the same 86%

  const bestOverallLpi = lpiSummary?.bestOverallLpi ?? 0
  const bestOverallLpiText = bestOverallLpi.toFixed(0)
  const bestOverallPercent = (bestOverallLpi / MaxLpi) * 100

  let hasAreaWithNoLpi = false

  const lpiAreaList = lpiSummary?.lpisByArea || []
  const lpiByArea: AreaProps[] = []
  const filteredAreas: FilteredAreaType[] = []
  let maxLpiFromAreas = 1 // this is used to adjust the percentage of progress relative to each other

  // filter, normalize, and calculate relative progress in a single pass
  lpiAreaList.forEach((areaData) => {
    const areaSlug = AreaSlugList.find((slug) => slug === areaData.areaSlug)
    if (!areaSlug) return

    const originalLpi = areaData.lpi ?? 0
    let lpi = originalLpi

    if (lpi < 0) {
      lpi = 0
    } else if (originalLpi >= 1 && originalLpi < 200) {
      lpi = 200
    }

    if (lpi > maxLpiFromAreas) {
      maxLpiFromAreas = lpi // this is used to adjust the percentage of progress relative to each other
    }

    filteredAreas.push({ areaSlug, lpi })
  })

  filteredAreas.forEach(({ areaSlug, lpi }) => {
    const notIncludedInLPI = areaSlug === AreaSlugMap.math.code
    const progress = Math.round((lpi / MaxLpi) * 100)
    const relativeProgress = Math.round((lpi / maxLpiFromAreas) * 100)

    if (lpi === 0 && !notIncludedInLPI) {
      // don't consider math area for overall lpi
      hasAreaWithNoLpi = true
    }

    lpiByArea.push({
      areaSlug,
      notIncludedInLPI,
      progress,
      relativeProgress,
      lpi,
      lpiText: lpi.toFixed(0),
      hasLpi: lpi > 0,
    })
  })

  const isOverallLpiAvailable = overallLpi > 0 && !hasAreaWithNoLpi

  return {
    loading,
    error,
    overallLpi,
    overallLpiText,
    overallPercent,
    bestOverallLpi,
    bestOverallLpiText,
    bestOverallPercent,
    hasAreaWithNoLpi,
    lpiByArea,
    isOverallLpiAvailable,
  }
}
